import { commaListSchema, filterDateRangeSchema } from "@birdwatch/common";
import { z } from "zod";
import {
  filterSearchFragment,
  notificationSearchFragment,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";

/**
 * The URL contract of `/office/quotes` (Phase 5, R-12).
 *
 * Every value is lenient (`z.string().optional()` and friends): a strict
 * `z.enum` on a search param throws `SearchParamError` and takes the whole page
 * into the error boundary on a stale bookmark (webapp-routing.md). `tab` and
 * `groupBy` are therefore narrowed inside the components, against the same two
 * closed vocabularies the list is built from.
 *
 * `filters` carries the canonical operator-nested wire form
 * (`filters[placeId][in]=a,b`); `createdAt` is a `dateRange`, whose operator is
 * derived from the bounds it fills, so it is NOT wrapped in `withOperators`.
 */
export const quoteListSearchSchema = z.object({
  search: z.string().optional(),
  /** drafts | open | accepted | expired | declined | all — default `drafts`. */
  tab: z.string().optional(),
  /** place | order | none — default `none`. */
  groupBy: z.string().optional(),
  /** The group keys the user collapsed, as a comma list. */
  collapse: commaListSchema,
  sort: z.string().optional(),
  take: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
  filters: z
    .object({
      placeId: withOperators(commaListSchema),
      accountId: withOperators(commaListSchema),
      createdAt: filterDateRangeSchema.catch(undefined),
      ...notificationSearchFragment,
    })
    .optional(),
  ...filterSearchFragment,
  ...maskSearchFragment,
});

export type QuoteListSearch = z.infer<typeof quoteListSearchSchema>;
